/**
 * Prepare from a source: paste the job description, upload a job requisition
 * (.txt / .md / .pdf / .docx), or give a link to a public job posting (only
 * where the server offers it). The server reads the text, treats it as data,
 * and answers a normalised context for the learner to check — it keeps
 * nothing. When a site cannot be read, the learner is told to paste instead.
 */
import { useState } from 'react';
import clsx from 'clsx';
import {
  usePrepareFromPasteMutation,
  usePrepareFromUploadMutation,
  usePrepareFromUrlMutation,
  type InterviewPrepCatalog,
  type PreparedSource,
} from '../../store/api/interviewPrepApi';
import { getErrorMessage } from '../../utils/errorHelpers';
import { availableSourceTabs, uploadProblem, type SourceTab as Tab } from '../../utils/interviewPrep';


interface Props {
  catalog: InterviewPrepCatalog;
  onPrepared: (prepared: PreparedSource) => void;
}

const TAB_LABEL: Record<Tab, string> = { paste: 'Paste text', upload: 'Upload a file', url: 'From a link' };

export function PrepSourcePanel({ catalog, onPrepared }: Props) {
  const tabs = availableSourceTabs(catalog);
  const [tab, setTab] = useState<Tab>(tabs[0] ?? 'paste');
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fromPaste, pasteState] = usePrepareFromPasteMutation();
  const [fromUpload, uploadState] = usePrepareFromUploadMutation();
  const [fromUrl, urlState] = usePrepareFromUrlMutation();
  const busy = pasteState.isLoading || uploadState.isLoading || urlState.isLoading;

  const sourceMax = catalog.limits.sourceMaxChars ?? 20000;
  const extensions = catalog.limits.uploadExtensions ?? ['.txt', '.md', '.markdown', '.pdf', '.docx'];
  const maxBytes = catalog.limits.uploadMaxBytes ?? 2 * 1024 * 1024;

  const prepare = async () => {
    setError(null);
    try {
      if (tab === 'paste') {
        onPrepared(await fromPaste({ text }).unwrap());
      } else if (tab === 'upload') {
        if (!file) return setError('Choose a file to upload.');
        const problem = uploadProblem(file, extensions, maxBytes);
        if (problem) return setError(problem);
        const form = new FormData();
        form.append('file', file);
        onPrepared(await fromUpload(form).unwrap());
      } else {
        onPrepared(await fromUrl({ url: url.trim() }).unwrap());
      }
    } catch (e) {
      setError(getErrorMessage(e, 'That source could not be read. Paste the job description text instead.'));
    }
  };

  return (
    <div className="space-y-3" data-testid="prep-source-panel">
      <div className="flex gap-2" role="tablist" aria-label="Preparation source">
        {tabs.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => {
              setTab(t);
              setError(null);
            }}
            className={clsx(
              'rounded-lg border px-3 py-1.5 text-sm',
              tab === t ? 'border-primary-500 bg-primary-50 text-primary-800' : 'border-gray-200 text-gray-700 hover:border-gray-300'
            )}
          >
            {TAB_LABEL[t]}
          </button>
        ))}
      </div>

      {tab === 'paste' && (
        <div>
          <label htmlFor="prep-paste" className="sr-only">Job description</label>
          <textarea
            id="prep-paste"
            value={text}
            maxLength={sourceMax}
            onChange={(e) => setText(e.target.value)}
            rows={8}
            placeholder="Paste the job description."
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
          />
          <div className="mt-1 text-right text-xs tabular-nums text-gray-500">{text.length}/{sourceMax}</div>
        </div>
      )}

      {tab === 'upload' && (
        <div>
          <label htmlFor="prep-file" className="block text-sm text-gray-700">
            Job requisition ({extensions.join(', ')}; up to {Math.round(maxBytes / (1024 * 1024))} MB)
          </label>
          <input
            id="prep-file"
            type="file"
            accept={extensions.join(',')}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="mt-1 block text-sm"
          />
          <p className="mt-1 text-xs text-gray-500">The file is read once to prepare your notes and is not kept.</p>
        </div>
      )}

      {tab === 'url' && (
        <div>
          <label htmlFor="prep-url" className="block text-sm text-gray-700">Link to a public job posting (https)</label>
          <input
            id="prep-url"
            type="url"
            inputMode="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://"
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
          />
          <p className="mt-1 text-xs text-gray-500">
            Pages that need you to sign in cannot be read. If that happens, paste the job description text instead.
          </p>
        </div>
      )}

      {error && (
        <p className="text-sm text-amber-900" role="alert" data-testid="prep-source-error">{error}</p>
      )}

      <button type="button" onClick={prepare} disabled={busy} className="btn-secondary px-4 py-2 text-sm disabled:opacity-50">
        {busy ? 'Reading…' : 'Prepare notes'}
      </button>
    </div>
  );
}

export default PrepSourcePanel;
